import type { SsoRedirectParams } from './ssoLogin';

/**
 * Transient-timeout SSO auto-retry (ENG-2910).
 *
 * Cognito's back-channel call to the IdP (PenFed PingFederate) intermittently
 * times out on a cold node and redirects back to us with
 * `error=invalid_request&error_description=Read timed out`. The login itself
 * succeeded at the IdP, so re-initiating /oauth2/authorize usually completes
 * silently (the IdP session cookie was just set) on a warmer node.
 *
 * This module owns two things and nothing else:
 *  - the retry *session* — the small piece of state that has to survive the
 *    redirect bounce (which login to re-attempt + how many tries so far); and
 *  - the pure decision of what to do when a callback error arrives.
 * The component (the SSO callback effect) is left as a thin executor of that
 * decision.
 */

const SESSION_KEY = 'sso_retry_session';

/** Max automatic retries before we stop and surface the error to the user. */
export const MAX_SSO_RETRIES = 2;

/**
 * Drop a session whose last activity was longer ago than this. sessionStorage is
 * already tab-scoped, so this only guards against a stale session lingering in
 * the same tab; `lastActivityAt` is refreshed on each retry so a slow multi-hop
 * sequence can't expire mid-flight.
 */
const SESSION_TTL_MS = 2 * 60 * 1000;

/** Errors worth retrying: Cognito's transient IdP-communication family only. */
const TRANSIENT_ERROR_RE = /timed out|timeout|connection reset/i;

/** The retry state as callers consume it (storage/timestamp details hidden). */
export interface SsoRetrySession {
  login: SsoRedirectParams;
  retryCount: number;
}

interface StoredSession extends SsoRetrySession {
  lastActivityAt: number;
}

function writeSession(session: StoredSession): boolean {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return true;
  } catch {
    // Storage unavailable (blocked / quota). Non-fatal: a session that can't be
    // written simply means no retry is available, which is the safe outcome.
    return false;
  }
}

/**
 * Start a fresh retry session for a login that is about to redirect to the IdP.
 * Called at initial login; resets the attempt count to zero. Stores no secrets.
 */
export function beginSsoRetrySession(login: SsoRedirectParams): void {
  writeSession({ login, retryCount: 0, lastActivityAt: Date.now() });
}

/**
 * Read the current retry session, or null if there isn't a usable one (absent,
 * corrupt, missing required fields, or stale past SESSION_TTL_MS).
 */
export function readSsoRetrySession(): SsoRetrySession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    const login = parsed.login;
    if (!login?.authorizeEndpoint || !login.clientId) return null;
    if (
      typeof parsed.lastActivityAt === 'number' &&
      Date.now() - parsed.lastActivityAt > SESSION_TTL_MS
    ) {
      return null;
    }
    return { login, retryCount: parsed.retryCount ?? 0 };
  } catch {
    // Corrupt session — treat as none.
    return null;
  }
}

/**
 * Record that another retry is being attempted: bump the count and refresh the
 * activity timestamp (so the TTL can't expire mid-sequence). Returns false if
 * there's no session or the write didn't persist — the caller must treat that as
 * "can't safely retry" and stop, since an un-incremented count can't bound the
 * loop.
 */
export function recordRetryAttempt(): boolean {
  const session = readSsoRetrySession();
  if (!session) return false;
  return writeSession({
    login: session.login,
    retryCount: session.retryCount + 1,
    lastActivityAt: Date.now(),
  });
}

export function clearSsoRetrySession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage unavailable; nothing to clear.
  }
}

/**
 * Whether an OAuth error_description is a transient IdP-communication timeout
 * worth retrying. Deliberately excludes permanent failures (access_denied,
 * invalid_scope, unsupported-config) which would only loop the user.
 */
export function isRetryableSsoError(errorDescription: string | null | undefined): boolean {
  if (!errorDescription) return false;
  return TRANSIENT_ERROR_RE.test(errorDescription);
}

/** What the callback should do about an SSO error redirect. */
export type SsoRetryDecision =
  | { kind: 'defer' } // retryable, but feature flags haven't resolved yet
  | { kind: 'retry'; login: SsoRedirectParams } // re-initiate the authorize redirect
  | { kind: 'giveUp'; exhausted: boolean }; // surface the error; exhausted = ran out of retries

/**
 * Decide what to do when the callback lands with an OAuth error. Pure: it reads
 * no storage and causes no side effects, so the branching can be unit-tested in
 * isolation (the executor in the component performs the resulting I/O).
 */
export function decideSsoRetryAction(input: {
  errorDescription: string | null | undefined;
  session: SsoRetrySession | null;
  featureFlagsLoaded: boolean;
  ssoAutoRetryEnabled: boolean;
}): SsoRetryDecision {
  const { errorDescription, session, featureFlagsLoaded, ssoAutoRetryEnabled } = input;

  const hasRetriesLeft = !!session && session.retryCount < MAX_SSO_RETRIES;
  const canRetry = isRetryableSsoError(errorDescription) && hasRetriesLeft;

  if (canRetry && !featureFlagsLoaded) return { kind: 'defer' };
  if (canRetry && ssoAutoRetryEnabled && session) return { kind: 'retry', login: session.login };

  const exhausted =
    isRetryableSsoError(errorDescription) && !!session && session.retryCount >= MAX_SSO_RETRIES;
  return { kind: 'giveUp', exhausted };
}
